import { createLedgerState, OWNER_KIND } from "../transactions/index";
import type { CommitResult, LedgerState, LeaseBook, TransferCommand } from "../transactions/index";
import type {
  ActionCancellation,
  ActionOpportunitySnapshot,
  ActionSelectionRequest,
  ActionState,
  ActiveActionAuthoritySnapshot,
  ActiveServiceContinuityBaseline,
  ActorActionStateSlot,
  ActorSample,
  DirectS03TransferProposal,
  OverlapFact,
  Phase7ArrivalObservation,
  Phase8Input,
  Phase8Output,
  Phase8StepResult,
  Phase9Input,
  Phase9Output,
  Phase9StepResult,
  PreauthorizedActionGrant,
  PresentationEvidenceEvent,
  PurchaseDwellReady,
  PurchaseEligibilitySnapshot,
  RecoveryResourceOwnerSource,
  S03PreviewRequest,
  TendReady,
  TendRecoveryAdmissionReceipt,
  TendRecoveryToken,
  TendRecoveryTokenKey,
  TendRecoveryResourceClaim,
  TriggerActionLifecycleSlice,
  UnstartedActionGrantRelease,
} from "./types";
import {
  assertArray,
  assertDeepFrozenPlain,
  assertNonNegative,
  assertPositive,
  assertRecord,
  checkedAdd,
  cloneFreeze,
  compareDistance,
  deepEqual,
} from "./validation";
import {
  assertActiveAuthority,
  assertArrivalObservation,
  assertGrantRecord,
  assertLifecycleSlice,
  assertOpportunityRecord,
  assertPhase8Output,
} from "./schema";

export interface ReducerDefinition {
  readonly definitionId: number;
  readonly triggerId: number;
  readonly nodeId: number;
  readonly semanticRuleId: number;
  readonly serviceKind: string;
  readonly actionKind: string;
  readonly authorityKind: string;
  readonly timingProfileId: number;
  readonly basePriorityClassRank: number;
  readonly purchaseOfferId: string | null;
  readonly runtimePlacement: Readonly<{
    readonly centerXMicroWU?: number; readonly centerYMicroWU?: number; readonly centerZMicroWU?: number; readonly radiusMicroWU?: number;
    readonly minXMicroWU?: number; readonly minYMicroWU?: number; readonly minZMicroWU?: number;
    readonly maxXMicroWU?: number; readonly maxYMicroWU?: number; readonly maxZMicroWU?: number;
  }>;
  readonly approachSocket: Readonly<{ xMicroWU: number; yMicroWU: number; zMicroWU: number }>;
}

export type ResolveDefinitions = (handles: readonly object[]) => readonly ReducerDefinition[];

const PHASE8_INPUT_KEYS = [
  "authorityTick", "previousSlice", "actorSamples", "definitionHandles", "opportunities",
  "phase7ArrivalObservations", "preauthorizedActionGrants", "activeActionAuthorities",
  "purchaseEligibilitySnapshots",
] as const;
const SLICE_KEYS = [
  "lastPhase8Tick", "currentOverlapFacts", "actionStateSlots", "actionCancellations",
  "tendRecoveryTokens", "actionSelectionRequests", "unstartedGrantReleases", "phase8Output", "phase9",
] as const;
const MEMBERSHIP_BASELINE_KEYS = [
  "actorId", "bodyId", "definitionId", "triggerId", "nodeId", "serviceId", "serviceVersion", "semanticRuleId",
  "serviceKind", "actionKind", "authorityKind", "timingProfileId", "priorityClassRank", "reservedTaskId", "workOrderId",
  "purchaseOfferId", "purchaseEligibility", "prerequisite",
] as const;
const GRANT_KEYS = [
  "grantTick", "authorizationBaseline", "provenanceKind", "sourceSelectionRequest", "serviceId", "serviceVersion", "enabled",
  "actorId", "bodyId", "definitionId", "triggerId", "nodeId", "actionId", "semanticRuleId", "serviceKind", "actionKind",
  "authorityKind", "timingProfileId", "priorityClassRank", "purchaseOfferId", "purchaseEligibility", "reservationLeaseId",
  "reservationLeaseVersion", "reservedTaskId", "workOrderId", "quantity", "workFact",
] as const;
const CONTINUITY_BASELINE_KEYS = [
  "authorizationKey", "semanticRuleId", "serviceKind", "actionKind", "authorityKind", "timingProfileId", "reservedTaskId",
  "workOrderId", "purchaseOfferId", "purchaseEligibility", "prerequisite",
] as const;
const ACTIVE_AUTHORITY_KEYS = [
  "authorityTick", "actorId", "bodyId", "actionId", "originalBaseline", "continuityBaseline", "currentContinuityBaseline",
  "authorityStatus", "releaseProof", "currentServiceVersion", "explicitCancel", "commandObservation", "leaseObservation",
] as const;
const RECOVERY_TOKEN_KEYS = [
  "status", "availableTick", "sourceCancellation", "priorPhase", "priorActorId", "priorBodyId", "priorActionId", "definitionId",
  "triggerId", "nodeId", "serviceId", "serviceVersion", "semanticRuleId", "serviceKind", "actionKind", "authorityKind",
  "timingProfileId", "priorityClassRank", "reservationLeaseId", "reservationLeaseVersion", "reservedTaskId", "workOrderId",
  "startTick", "minimumContactTick", "authorityEligibleTick", "contactTick", "quantity", "priorWorkFact", "selectionRequest",
] as const;
const RECOVERY_RECEIPT_KEYS = [
  "admissionTick", "authorizationPath", "recoveryProof", "consumedToken", "sourceCancellation", "priorActorId", "priorBodyId",
  "priorActionId", "newActorId", "newBodyId", "newActionId", "definitionId", "triggerId", "nodeId", "serviceId",
  "serviceVersion", "admissionGrant", "continuityBaseline", "priorWorkFact", "activeWorkFact",
] as const;
const PURCHASE_ORDER = ["pen-extension", "bigger-basket", "hire-generalist", "open-second-yard"] as const;
const PURCHASE_OFFERS = Object.freeze({
  "pen-extension": Object.freeze({ priceCash: 24, prerequisiteId: "first-sale-completed", effectId: "fixed-pen-capacity-4-to-5-fifth-bay-chicken", settlementKind: "nav-pending-intent" }),
  "bigger-basket": Object.freeze({ priceCash: 64, prerequisiteId: "pen-extension-completed", effectId: "player-basket-slots-5-to-8", settlementKind: "ordinary-purchase" }),
  "hire-generalist": Object.freeze({ priceCash: 360, prerequisiteId: "bigger-basket-completed", effectId: "farm-hand-count-0-to-1-generalist-task-eligible-after-22-ticks", settlementKind: "ordinary-purchase" }),
  "open-second-yard": Object.freeze({ priceCash: 520, prerequisiteId: "first-hire-completed", effectId: "second-yard-walkability-and-row-2-unlocking-36", settlementKind: "nav-pending-intent" }),
} as const);

function assertCanonicalOrder<T>(
  values: readonly T[],
  compare: (left: T, right: T) => number,
  label: string,
): void {
  for (let index = 1; index < values.length; index += 1) {
    if (compare(values[index - 1]!, values[index]!) >= 0) throw new TypeError(`${label} is not canonical`);
  }
}

function assertFrozenCollection(value: unknown, label: string): void {
  assertArray(value, label);
  if (!Object.isFrozen(value)) throw new TypeError(`${label} must be frozen`);
}

function assertGrantRecordShape(grant: PreauthorizedActionGrant): void {
  assertGrantRecord(grant);
}

function assertActiveAuthorityShape(authority: ActiveActionAuthoritySnapshot): void {
  assertActiveAuthority(authority);
}

function assertSlice(value: unknown): asserts value is TriggerActionLifecycleSlice {
  assertLifecycleSlice(value);
}

function assertActorSamples(values: readonly ActorSample[]): void {
  assertArray(values, "actorSamples");
  assertCanonicalOrder(values, (a, b) => a.actorId - b.actorId || a.bodyId - b.bodyId, "actorSamples");
  const bodyIds = new Set<number>();
  const actorIds = new Set<number>();
  for (const actor of values) {
    assertRecord(actor, [
      "actorId", "bodyId", "humanoidAuthorityClass", "actorKind", "enabled", "present",
      "bodyVisible", "xMicroWU", "approachYMicroWU", "zMicroWU",
    ], "actor sample");
    assertPositive(actor.actorId, "actorId"); assertPositive(actor.bodyId, "bodyId");
    if (actorIds.has(actor.actorId)) throw new TypeError("actor identity cannot have multiple bodies in one snapshot");
    if (bodyIds.has(actor.bodyId)) throw new TypeError("body identity cannot alias actors");
    actorIds.add(actor.actorId);
    bodyIds.add(actor.bodyId);
    if (actor.humanoidAuthorityClass !== "HumanoidActor" || !["Player", "FarmHand"].includes(actor.actorKind)) throw new TypeError("invalid actor kind");
    if (actor.enabled !== true || actor.present !== true || actor.bodyVisible !== true) throw new TypeError("actor must be available");
    for (const coordinate of [actor.xMicroWU, actor.approachYMicroWU, actor.zMicroWU]) {
      if (!Number.isSafeInteger(coordinate) || Object.is(coordinate, -0)) throw new TypeError("invalid actor coordinate");
    }
  }
}

function assertPurchaseSnapshots(values: readonly PurchaseEligibilitySnapshot[]): void {
  assertArray(values, "purchaseEligibilitySnapshots");
  let previous = -1;
  let eligible = 0;
  for (const snapshot of values) {
    assertRecord(snapshot, ["purchaseOfferId", "consumerEligibilityVersion", "consumerEligible", "walletBalanceCash", "walletVersion"], "purchase eligibility");
    const ordinal = PURCHASE_ORDER.indexOf(snapshot.purchaseOfferId);
    if (ordinal <= previous) throw new TypeError("purchase snapshots are not in declaration order");
    previous = ordinal;
    if (snapshot.consumerEligible) eligible += 1;
    if (eligible > 1) throw new TypeError("multiple consumer eligible purchase rows");
    if (!Number.isSafeInteger(snapshot.consumerEligibilityVersion) || snapshot.consumerEligibilityVersion < 0 ||
        !Number.isSafeInteger(snapshot.walletBalanceCash) || snapshot.walletBalanceCash < 0 ||
        !Number.isSafeInteger(snapshot.walletVersion) || snapshot.walletVersion < 0 ||
        typeof snapshot.consumerEligible !== "boolean") throw new TypeError("invalid purchase snapshot");
  }
}

function authorizationKey(value: ActionOpportunitySnapshot | Phase7ArrivalObservation): string {
  const key = "authorizationBaseline" in value ? value.authorizationBaseline.authorizationKey : value.authorizationKey;
  return [key.actorId, key.bodyId, key.definitionId, key.triggerId, key.nodeId, key.serviceId, key.serviceVersion].join(":");
}

function assertObservation(observation: Phase7ArrivalObservation, tick: number): void {
  assertArrivalObservation(observation);
  if (observation.observationTick !== tick) throw new TypeError("observation tick mismatch");
}

function definitionById(definitions: readonly ReducerDefinition[], id: number): ReducerDefinition {
  return definitions[id - 1]!;
}

function distanceToDefinition(actor: ActorSample, definition: ReducerDefinition): string {
  const dx = BigInt(actor.xMicroWU) - BigInt(definition.approachSocket.xMicroWU);
  const dz = BigInt(actor.zMicroWU) - BigInt(definition.approachSocket.zMicroWU);
  return (dx * dx + dz * dz).toString(10);
}

function isMember(actor: ActorSample, definition: ReducerDefinition, retain: boolean): boolean {
  const placement = definition.runtimePlacement;
  const verticalDistance = BigInt(actor.approachYMicroWU) - BigInt(definition.approachSocket.yMicroWU);
  if ((verticalDistance < 0n ? -verticalDistance : verticalDistance) > (retain ? 650_000n : 500_000n)) return false;
  const hysteresis = retain ? 150_000 : 0;
  if (placement.centerXMicroWU !== undefined) {
    const dx = BigInt(actor.xMicroWU) - BigInt(placement.centerXMicroWU);
    const dz = BigInt(actor.zMicroWU) - BigInt(placement.centerZMicroWU!);
    const radius = BigInt(placement.radiusMicroWU! + hysteresis);
    const y = actor.approachYMicroWU;
    return dx * dx + dz * dz <= radius * radius && y >= placement.centerYMicroWU! - placement.radiusMicroWU! - hysteresis && y <= placement.centerYMicroWU! + placement.radiusMicroWU! + hysteresis;
  }
  return actor.xMicroWU >= placement.minXMicroWU! - hysteresis && actor.xMicroWU <= placement.maxXMicroWU! + hysteresis &&
    actor.zMicroWU >= placement.minZMicroWU! - hysteresis && actor.zMicroWU <= placement.maxZMicroWU! + hysteresis &&
    actor.approachYMicroWU >= placement.minYMicroWU! - hysteresis && actor.approachYMicroWU <= placement.maxYMicroWU! + hysteresis;
}

function assertOpportunity(
  opportunity: ActionOpportunitySnapshot,
  tick: number,
  actors: readonly ActorSample[],
  definitions: readonly ReducerDefinition[],
  observationByKey: ReadonlyMap<string, Phase7ArrivalObservation>,
  priorFacts: ReadonlyMap<string, OverlapFact>,
  previousTick: number | null,
): void {
  assertOpportunityRecord(opportunity);
  const actor = actors.find((candidate) => candidate.actorId === opportunity.actorId);
  if (actor === undefined || actor.bodyId !== opportunity.bodyId) throw new TypeError("opportunity actor mismatch");
  const definition = definitionById(definitions, opportunity.definitionId);
  const identity = [opportunity.actorId, opportunity.bodyId, opportunity.definitionId, opportunity.triggerId, opportunity.nodeId, opportunity.serviceId].join(":");
  const prior = priorFacts.get(identity);
  if (prior !== undefined && prior.membershipBaseline.serviceVersion === opportunity.serviceVersion &&
      !deepEqual(prior.membershipBaseline, opportunity.authorizationBaseline.membershipBaseline)) {
    throw new TypeError("equal serviceVersion cannot change a retained authorization baseline");
  }
  const retaining = prior !== undefined && previousTick !== null && prior.lastRetainedTick === previousTick && deepEqual(prior.membershipBaseline, opportunity.authorizationBaseline.membershipBaseline);
  if (!isMember(actor, definition, retaining)) throw new TypeError(`opportunity is outside ${retaining ? "retain" : "ENTER"} geometry`);
  if (definition.actionKind === "purchase-dwell" && actor.actorKind !== "Player") throw new TypeError("FarmHand cannot use purchase pads");
  if (opportunity.triggerId !== definition.triggerId || opportunity.nodeId !== definition.nodeId ||
      opportunity.semanticRuleId !== definition.semanticRuleId || opportunity.serviceKind !== definition.serviceKind ||
      opportunity.actionKind !== definition.actionKind || opportunity.authorityKind !== definition.authorityKind ||
      opportunity.timingProfileId !== definition.timingProfileId || opportunity.basePriorityClassRank !== definition.basePriorityClassRank ||
      opportunity.purchaseOfferId !== definition.purchaseOfferId) throw new TypeError("opportunity definition mismatch");
  if (opportunity.opportunityTick !== tick || opportunity.matchKey.opportunityTick !== tick) throw new TypeError("opportunity tick mismatch");
  const observation = observationByKey.get(authorizationKey(opportunity));
  if (observation === undefined || !deepEqual(observation, opportunity.arrivalObservation) || observation.status !== "eligible" ||
      opportunity.arrivalEligibleTick !== observation.arrivalEligibleTick) throw new TypeError("opportunity arrival mismatch");
  const expectedDistance = distanceToDefinition(actor, definition);
  if (opportunity.distanceSquaredMicroWU2 !== expectedDistance) throw new TypeError("fabricated opportunity distance");
  const reservation = opportunity.authorizationBaseline.priorityBasis.farmHandReservationProof;
  if (reservation !== null) {
    if (actor.actorKind !== "FarmHand") throw new TypeError("invalid FarmHand reservation proof");
  }
}

function assertGrant(
  grant: PreauthorizedActionGrant,
  tick: number,
  actors: readonly ActorSample[],
  definitions: readonly ReducerDefinition[],
  observationByKey: ReadonlyMap<string, Phase7ArrivalObservation>,
): void {
  assertGrantRecordShape(grant);
  if (grant.grantTick !== tick) throw new TypeError("grant tick mismatch");
  const actor = actors.find((candidate) => candidate.actorId === grant.actorId);
  if (actor === undefined || actor.bodyId !== grant.bodyId) throw new TypeError("grant actor mismatch");
  const definition = definitionById(definitions, grant.definitionId);
  if (grant.triggerId !== definition.triggerId || grant.nodeId !== definition.nodeId ||
      grant.semanticRuleId !== definition.semanticRuleId || grant.serviceKind !== definition.serviceKind ||
      grant.actionKind !== definition.actionKind || grant.authorityKind !== definition.authorityKind ||
      grant.timingProfileId !== definition.timingProfileId || grant.purchaseOfferId !== definition.purchaseOfferId) {
    throw new TypeError("grant definition mismatch");
  }
  const observation = observationByKey.get(authorizationKey({ authorizationBaseline: grant.authorizationBaseline } as ActionOpportunitySnapshot));
  if (observation === undefined) throw new TypeError("orphan grant without Phase7 observation");
  if ((grant.workFact.kind === "direct-transfer" || grant.workFact.kind === "collect") && grant.workFact.transferCommand.cancelled !== false) {
    throw new TypeError("admission command must not be cancelled");
  }
}

function opportunityCompare(left: ActionOpportunitySnapshot, right: ActionOpportunitySnapshot): number {
  return left.actorId - right.actorId || left.bodyId - right.bodyId || left.definitionId - right.definitionId ||
    left.triggerId - right.triggerId || left.nodeId - right.nodeId || left.serviceId - right.serviceId;
}
function winnerCompare(left: ActionOpportunitySnapshot, right: ActionOpportunitySnapshot): number {
  return left.priorityClassRank - right.priorityClassRank || compareDistance(left.distanceSquaredMicroWU2, right.distanceSquaredMicroWU2) ||
    left.nodeId - right.nodeId || left.definitionId - right.definitionId || left.triggerId - right.triggerId || left.serviceId - right.serviceId;
}

function continuityFromGrant(grant: PreauthorizedActionGrant): ActiveServiceContinuityBaseline {
  const membership = grant.authorizationBaseline.membershipBaseline;
  return cloneFreeze({
    authorizationKey: grant.authorizationBaseline.authorizationKey,
    semanticRuleId: membership.semanticRuleId,
    serviceKind: membership.serviceKind,
    actionKind: membership.actionKind,
    authorityKind: membership.authorityKind,
    timingProfileId: membership.timingProfileId,
    reservedTaskId: membership.reservedTaskId,
    workOrderId: membership.workOrderId,
    purchaseOfferId: membership.purchaseOfferId,
    purchaseEligibility: membership.purchaseEligibility,
    prerequisite: membership.prerequisite,
  });
}

function delayForGrant(grant: PreauthorizedActionGrant): number {
  if (grant.actionKind === "collect-unit") return 6;
  if (grant.actionKind === "stage-batch") return checkedAdd(4, 2 * (grant.quantity as number), "stage delay");
  if (grant.actionKind === "tend-wave") return 36;
  return 10;
}

function startAction(
  tick: number,
  grant: PreauthorizedActionGrant,
  tendRecoveryAdmissionReceipt: TendRecoveryAdmissionReceipt | null = null,
): ActionState {
  const minimumContactTick = checkedAdd(tick, 3, "minimum contact tick");
  return cloneFreeze({
    phase: 1,
    actorId: grant.actorId,
    bodyId: grant.bodyId,
    definitionId: grant.definitionId,
    triggerId: grant.triggerId,
    nodeId: grant.nodeId,
    actionId: grant.actionId,
    serviceBaseline: grant,
    continuityBaseline: continuityFromGrant(grant),
    tendRecoveryAdmissionReceipt,
    semanticRuleId: grant.semanticRuleId,
    serviceKind: grant.serviceKind,
    actionKind: grant.actionKind,
    authorityKind: grant.authorityKind,
    timingProfileId: grant.timingProfileId,
    priorityClassRank: grant.priorityClassRank,
    purchaseOfferId: grant.purchaseOfferId,
    purchaseEligibility: grant.purchaseEligibility,
    startTick: tick,
    minimumContactTick,
    authorityEligibleTick: checkedAdd(tick, delayForGrant(grant), "authority eligible tick"),
    contactTick: null,
    quantity: grant.quantity,
    workFact: grant.workFact,
  });
}

function recoveryTokenKey(value: TendRecoveryToken | TendRecoveryTokenKey): string {
  return `${value.availableTick}:${value.priorActorId}:${value.priorActionId}`;
}

function tokenFromCancellation(state: ActionState, cancellation: ActionCancellation): TendRecoveryToken {
  const grant = state.serviceBaseline;
  return cloneFreeze({
    status: "available",
    availableTick: checkedAdd(cancellation.cancellationTick, 1, "recovery available tick"),
    sourceCancellation: cancellation,
    priorPhase: state.phase,
    priorActorId: state.actorId,
    priorBodyId: state.bodyId,
    priorActionId: state.actionId,
    definitionId: state.definitionId,
    triggerId: state.triggerId,
    nodeId: state.nodeId,
    serviceId: grant.serviceId,
    serviceVersion: grant.serviceVersion,
    semanticRuleId: state.semanticRuleId,
    serviceKind: "pen-tend",
    actionKind: "tend-wave",
    authorityKind: "node-reaction",
    timingProfileId: state.timingProfileId,
    priorityClassRank: state.priorityClassRank,
    reservationLeaseId: grant.reservationLeaseId,
    reservationLeaseVersion: grant.reservationLeaseVersion,
    reservedTaskId: grant.reservedTaskId,
    workOrderId: grant.workOrderId,
    startTick: state.startTick,
    minimumContactTick: state.minimumContactTick,
    authorityEligibleTick: state.authorityEligibleTick,
    contactTick: state.contactTick,
    quantity: state.quantity,
    priorWorkFact: state.workFact as Extract<ActionState["workFact"], { kind: "tend" }>,
    selectionRequest: null,
  }) as TendRecoveryToken;
}

function consumedToken(token: TendRecoveryToken): TendRecoveryToken {
  return cloneFreeze({ ...token, status: "consumed" }) as TendRecoveryToken;
}

function pendingToken(token: TendRecoveryToken, request: ActionSelectionRequest): TendRecoveryToken {
  return cloneFreeze({ ...token, status: "selection-pending", selectionRequest: request }) as TendRecoveryToken;
}

function tokenRecord(token: TendRecoveryToken): Readonly<Record<string, unknown>> {
  return token as unknown as Readonly<Record<string, unknown>>;
}

function proofKey(opportunity: ActionOpportunitySnapshot): TendRecoveryTokenKey | null {
  return opportunity.authorizationBaseline.priorityBasis.tendRecoveryProof?.recoveryTokenKey ?? null;
}

interface RecoveryArbitration {
  readonly token: TendRecoveryToken;
  readonly owner: RecoveryResourceOwnerSource;
  readonly selected: ActionOpportunitySnapshot | null;
  readonly claims: readonly ActionOpportunitySnapshot[];
}

function compareRecoveryClaim(left: ActionOpportunitySnapshot, right: ActionOpportunitySnapshot): number {
  return compareDistance(left.distanceSquaredMicroWU2, right.distanceSquaredMicroWU2) ||
    left.actorId - right.actorId;
}

function liveRecoveryArbitrations(
  tick: number,
  tokens: readonly TendRecoveryToken[],
  opportunities: readonly ActionOpportunitySnapshot[],
): readonly RecoveryArbitration[] {
  const results: RecoveryArbitration[] = [];
  for (const token of tokens) {
    const key = recoveryTokenKey(token);
    if (token.status === "consumed") {
      continue;
    }
    const claims = opportunities.filter((opportunity) => {
      const proof = proofKey(opportunity);
      return proof !== null && recoveryTokenKey(proof) === key;
    });
    const tokenData = tokenRecord(token);
    for (const claim of claims) {
      if (claim.definitionId !== tokenData.definitionId || claim.triggerId !== tokenData.triggerId ||
          claim.nodeId !== tokenData.nodeId || claim.serviceId !== tokenData.serviceId ||
          claim.serviceVersion !== tokenData.serviceVersion || claim.semanticRuleId !== tokenData.semanticRuleId ||
          claim.serviceKind !== tokenData.serviceKind || claim.actionKind !== tokenData.actionKind ||
          claim.authorityKind !== tokenData.authorityKind || claim.timingProfileId !== tokenData.timingProfileId ||
          claim.reservedTaskId !== tokenData.reservedTaskId || claim.workOrderId !== tokenData.workOrderId ||
          !deepEqual(claim.prerequisite.tendWorkFact, tokenData.priorWorkFact)) {
        throw new TypeError("recovery claim changes token-bound wave facts");
      }
    }
    if (token.status === "available") {
      if (claims.length === 0) continue;
      const selected = [...claims].sort(compareRecoveryClaim)[0]!;
      const claim = cloneFreeze({
        tokenKey: proofKey(selected)!, status: "available", claimantOpportunity: selected,
        distanceSquaredMicroWU2: selected.distanceSquaredMicroWU2, actorId: selected.actorId, bodyId: selected.bodyId,
      }) as TendRecoveryResourceClaim;
      const owner = cloneFreeze({
        ownerKind: "available-claim", tokenKey: claim.tokenKey, actorId: selected.actorId, bodyId: selected.bodyId,
        authorizationBaseline: selected.authorizationBaseline, availableClaim: claim, selectionRequest: null,
      }) as RecoveryResourceOwnerSource;
      results.push({ token, owner, selected, claims });
      continue;
    }
    const baseline = token.selectionRequest!.authorizationBaseline;
    const selected = claims.find((claim) => claim.actorId === baseline.authorizationKey.actorId &&
      claim.bodyId === baseline.authorizationKey.bodyId && deepEqual(claim.authorizationBaseline, baseline)) ?? null;
    const owner = cloneFreeze({
      ownerKind: "selection-pending-request", tokenKey: baseline.priorityBasis.tendRecoveryProof!.recoveryTokenKey,
      actorId: baseline.authorizationKey.actorId, bodyId: baseline.authorizationKey.bodyId,
      authorizationBaseline: baseline, availableClaim: null, selectionRequest: token.selectionRequest,
    }) as RecoveryResourceOwnerSource;
    results.push({ token, owner, selected, claims });
  }
  return results;
}

function createRecoveryReceipt(
  tick: number,
  token: TendRecoveryToken,
  grant: PreauthorizedActionGrant,
): TendRecoveryAdmissionReceipt {
  const proof = grant.authorizationBaseline.priorityBasis.tendRecoveryProof!;
  const consumed = consumedToken(token);
  const path = token.status === "selection-pending" ? "deferred-selection" : "direct-preauthorized";
  if (path === "deferred-selection") {
    if (grant.provenanceKind !== "selection-request" || token.selectionRequest === null ||
        !deepEqual(grant.sourceSelectionRequest, token.selectionRequest)) throw new TypeError("deferred recovery request mismatch");
  } else if (grant.provenanceKind !== "scored-preauthorization" || grant.sourceSelectionRequest !== null) {
    throw new TypeError("direct recovery provenance mismatch");
  }
  const prior = tokenRecord(token);
  const receipt = cloneFreeze({
    admissionTick: tick,
    authorizationPath: path,
    recoveryProof: proof,
    consumedToken: consumed,
    sourceCancellation: prior.sourceCancellation,
    priorActorId: token.priorActorId,
    priorBodyId: prior.priorBodyId,
    priorActionId: token.priorActionId,
    newActorId: grant.actorId,
    newBodyId: grant.bodyId,
    newActionId: grant.actionId,
    definitionId: grant.definitionId,
    triggerId: grant.triggerId,
    nodeId: grant.nodeId,
    serviceId: grant.serviceId,
    serviceVersion: grant.serviceVersion,
    admissionGrant: grant,
    continuityBaseline: continuityFromGrant(grant),
    priorWorkFact: prior.priorWorkFact,
    activeWorkFact: grant.workFact,
  });
  return receipt as TendRecoveryAdmissionReceipt;
}

function cancellationFrom(state: ActionState, tick: number, reason: ActionCancellation["reason"]): ActionCancellation {
  return cloneFreeze({
    cancellationTick: tick,
    actorId: state.actorId,
    actionId: state.actionId,
    definitionId: state.definitionId,
    triggerId: state.triggerId,
    serviceId: state.serviceBaseline.serviceId,
    serviceVersion: state.serviceBaseline.serviceVersion,
    reservationLeaseId: state.serviceBaseline.reservationLeaseId,
    reservationLeaseVersion: state.serviceBaseline.reservationLeaseVersion,
    reason,
  });
}

function validateActiveState(
  state: ActionState,
  authority: ActiveActionAuthoritySnapshot,
  actor: ActorSample | undefined,
  tick: number,
  observation: Phase7ArrivalObservation | undefined,
  definition: ReducerDefinition,
): { readonly valid: boolean; readonly terminal: boolean; readonly reason?: ActionCancellation["reason"] } {
  if (authority.authorityTick !== tick) throw new TypeError("active authority tick mismatch");
  if (!deepEqual(authority.originalBaseline, state.serviceBaseline) || !deepEqual(authority.continuityBaseline, state.continuityBaseline)) throw new TypeError("active authority baseline mismatch");
  if (state.phase === 3 && (authority.authorityStatus !== "released" || authority.releaseProof?.cause !== "normal-terminal" ||
      authority.releaseProof.sourcePhase !== 6 || authority.explicitCancel)) {
    throw new TypeError("phase3 state requires an exact normal-terminal release");
  }
  if (authority.authorityStatus === "released") {
    const proof = authority.releaseProof!;
    if (proof.cause === "actor-removal" && actor !== undefined) throw new TypeError("actor-removal proof is illegal while actor remains present");
    if (proof.cause === "normal-terminal" && proof.sourcePhase === 6 && state.phase === 3 && !authority.explicitCancel) return { valid: false, terminal: true };
    if (proof.cause === "explicit-release" && (proof.sourcePhase === 1 || proof.sourcePhase === 6) && authority.explicitCancel) return { valid: false, terminal: false, reason: "explicit-cancel" };
    if (proof.cause === "actor-removal" && proof.sourcePhase === 1 && !authority.explicitCancel) return { valid: false, terminal: false, reason: "actor-unavailable" };
    throw new TypeError("invalid active release");
  }
  if (authority.explicitCancel) return { valid: false, terminal: false, reason: "explicit-cancel" };
  if (actor === undefined) throw new TypeError("removed actor requires authenticated release proof");
  if (actor.bodyId !== state.bodyId || authority.bodyId !== state.bodyId) return { valid: false, terminal: false, reason: "body-identity-changed" };
  const continuityChanged = !deepEqual(authority.currentContinuityBaseline, state.continuityBaseline);
  const versionChanged = authority.currentServiceVersion !== state.serviceBaseline.serviceVersion;
  if (continuityChanged !== versionChanged || (versionChanged && authority.currentServiceVersion <= state.serviceBaseline.serviceVersion)) {
    throw new TypeError("active stable-byte/service-version invariant failed");
  }
  if (continuityChanged) {
    const currentPrerequisite = authority.currentContinuityBaseline.prerequisite;
    const priorPrerequisite = state.continuityBaseline.prerequisite;
    const normalizedCurrentPrerequisite = currentPrerequisite.purchaseWorkFact === null ? currentPrerequisite : {
      ...currentPrerequisite,
      purchaseWorkFact: { ...currentPrerequisite.purchaseWorkFact, purchaseEligibility: priorPrerequisite.purchaseWorkFact!.purchaseEligibility },
    };
    const currentWithoutPurchase = {
      ...authority.currentContinuityBaseline,
      authorizationKey: state.continuityBaseline.authorizationKey,
      purchaseEligibility: state.continuityBaseline.purchaseEligibility,
      prerequisite: normalizedCurrentPrerequisite,
    };
    const purchaseChanged = state.actionKind === "purchase-dwell" && !deepEqual(authority.currentContinuityBaseline.purchaseEligibility, state.purchaseEligibility) && deepEqual(currentWithoutPurchase, state.continuityBaseline);
    return { valid: false, terminal: false, reason: purchaseChanged ? "purchase-eligibility-changed" : "service-baseline-changed" };
  }
  if (["missing", "changed", "cancelled"].includes(authority.commandObservation.status)) return { valid: false, terminal: false, reason: "command-changed-or-cancelled" };
  if (["missing", "changed", "expired"].includes(authority.leaseObservation.status)) return { valid: false, terminal: false, reason: "lease-changed-or-expired" };
  if (observation === undefined) throw new TypeError("active state lacks Phase7 observation");
  if (observation.status !== "eligible" || !isMember(actor, definition, true)) return { valid: false, terminal: false, reason: "membership-lost" };
  let safeTiming = true;
  try {
    safeTiming = state.minimumContactTick === checkedAdd(state.startTick, 3, "active contact timing") &&
      state.startTick === state.serviceBaseline.grantTick &&
      state.authorityEligibleTick === checkedAdd(state.startTick, delayForGrant(state.serviceBaseline), "active authority timing") &&
      (state.contactTick === null || state.contactTick === state.minimumContactTick) &&
      (state.phase === 1 ? state.contactTick === null && tick <= state.minimumContactTick : true) &&
      (state.phase === 2 ? state.contactTick === state.minimumContactTick && tick <= state.authorityEligibleTick : true) &&
      (state.phase === 3 ? state.contactTick === state.minimumContactTick && tick > state.authorityEligibleTick : true);
  } catch { safeTiming = false; }
  if (!safeTiming) return { valid: false, terminal: false, reason: "unsafe-timing" };
  return { valid: true, terminal: false };
}

function progressAction(state: ActionState, tick: number): {
  readonly state: ActionState;
  readonly preview: S03PreviewRequest | null;
  readonly tend: TendReady | null;
  readonly purchase: PurchaseDwellReady | null;
  readonly presentation: PresentationEvidenceEvent | null;
} {
  let progressed = state;
  if (tick >= state.minimumContactTick && state.phase === 1) progressed = cloneFreeze({ ...state, phase: 2, contactTick: state.minimumContactTick });
  if (tick !== state.authorityEligibleTick || progressed.phase === 3) return { state: progressed, preview: null, tend: null, purchase: null, presentation: null };
  progressed = cloneFreeze({ ...progressed, phase: 3 });
  const presentation = cloneFreeze({
    kind: "primary-action-presentation-evidence",
    actorId: state.actorId, actionId: state.actionId, actionKind: state.actionKind,
    startTick: state.startTick, minimumContactTick: state.minimumContactTick, authorityTick: tick,
    minimumFollowThroughMilliseconds: 180, nonAuthoritative: true, nonBlocking: true,
  }) as PresentationEvidenceEvent;
  if (state.authorityKind === "s03-transfer") {
    const preview = cloneFreeze({
      kind: "s03-preview-request", authorityTick: tick, authorityKind: "s03-transfer",
      actorId: state.actorId, bodyId: state.bodyId, actionId: state.actionId,
      serviceId: state.serviceBaseline.serviceId, serviceVersion: state.serviceBaseline.serviceVersion,
      triggerId: state.triggerId, nodeId: state.nodeId, workFact: state.workFact,
    }) as S03PreviewRequest;
    return { state: progressed, preview, tend: null, purchase: null, presentation };
  }
  if (state.actionKind === "tend-wave") {
    const tend = cloneFreeze({
      kind: "tend-ready", authorityTick: tick, actorId: state.actorId, bodyId: state.bodyId,
      actionId: state.actionId, serviceId: state.serviceBaseline.serviceId,
      serviceVersion: state.serviceBaseline.serviceVersion, triggerId: state.triggerId, nodeId: state.nodeId,
      reservationLeaseId: state.serviceBaseline.reservationLeaseId,
      reservationLeaseVersion: state.serviceBaseline.reservationLeaseVersion,
      reservedTaskId: state.serviceBaseline.reservedTaskId, workOrderId: state.serviceBaseline.workOrderId,
      workFact: state.workFact,
    }) as TendReady;
    return { state: progressed, preview: null, tend, purchase: null, presentation };
  }
  const work = state.workFact as Extract<ActionState["workFact"], { kind: "purchase" }>;
  const purchase = cloneFreeze({
    kind: "purchase-dwell-ready", authorityTick: tick, actorId: state.actorId, bodyId: state.bodyId,
    actionId: state.actionId, serviceId: state.serviceBaseline.serviceId,
    serviceVersion: state.serviceBaseline.serviceVersion, triggerId: state.triggerId, nodeId: state.nodeId,
    purchaseOfferId: work.purchaseOfferId, priceCash: work.priceCash, prerequisiteId: work.prerequisiteId,
    effectId: work.effectId, settlementKind: work.settlementKind, purchaseEligibility: work.purchaseEligibility, quantity: null,
  }) as PurchaseDwellReady;
  return { state: progressed, preview: null, tend: null, purchase, presentation };
}

function hasExactTerminalHandoff(state: ActionState, output: Phase8Output): boolean {
  if (output.authorityTick !== state.authorityEligibleTick) return false;
  const identity = (value: { readonly actorId: number; readonly actionId: number }) => value.actorId === state.actorId && value.actionId === state.actionId;
  if (state.authorityKind === "s03-transfer") {
    const matches = output.previewRequests.filter(identity);
    return matches.length === 1 && matches[0]!.authorityTick === state.authorityEligibleTick && matches[0]!.bodyId === state.bodyId &&
      matches[0]!.serviceId === state.serviceBaseline.serviceId && matches[0]!.serviceVersion === state.serviceBaseline.serviceVersion &&
      matches[0]!.triggerId === state.triggerId && matches[0]!.nodeId === state.nodeId && deepEqual(matches[0]!.workFact, state.workFact);
  }
  if (state.actionKind === "tend-wave") {
    const matches = output.tendReady.filter(identity);
    return matches.length === 1 && matches[0]!.authorityTick === state.authorityEligibleTick && matches[0]!.bodyId === state.bodyId &&
      matches[0]!.serviceId === state.serviceBaseline.serviceId && matches[0]!.serviceVersion === state.serviceBaseline.serviceVersion &&
      matches[0]!.triggerId === state.triggerId && matches[0]!.nodeId === state.nodeId && deepEqual(matches[0]!.workFact, state.workFact);
  }
  const matches = output.purchaseDwellReady.filter(identity);
  const work = state.workFact;
  return work.kind === "purchase" && matches.length === 1 && matches[0]!.authorityTick === state.authorityEligibleTick &&
    matches[0]!.bodyId === state.bodyId && matches[0]!.serviceId === state.serviceBaseline.serviceId &&
    matches[0]!.serviceVersion === state.serviceBaseline.serviceVersion && matches[0]!.triggerId === state.triggerId &&
    matches[0]!.nodeId === state.nodeId && matches[0]!.purchaseOfferId === work.purchaseOfferId &&
    matches[0]!.priceCash === work.priceCash && deepEqual(matches[0]!.purchaseEligibility, work.purchaseEligibility);
}

export function reducePhase8(input: Phase8Input, resolveDefinitions: ResolveDefinitions): Phase8StepResult {
  assertRecord(input, PHASE8_INPUT_KEYS, "Phase8Input");
  assertPositive(input.authorityTick, "authorityTick");
  assertSlice(input.previousSlice);
  for (const field of ["actorSamples", "definitionHandles", "opportunities", "phase7ArrivalObservations", "preauthorizedActionGrants", "activeActionAuthorities", "purchaseEligibilitySnapshots"] as const) {
    assertFrozenCollection(input[field], `Phase8Input.${field}`);
  }
  const previousTick = input.previousSlice.lastPhase8Tick;
  if (previousTick !== null && input.authorityTick <= previousTick) throw new TypeError("Phase8 tick must advance");
  if (previousTick !== null && input.previousSlice.phase9.status !== "consumed") throw new TypeError("previous Phase9 must be consumed");
  const gap = previousTick !== null && input.authorityTick > checkedAdd(previousTick, 1, "lifecycle continuity");
  assertActorSamples(input.actorSamples);
  assertDeepFrozenPlain(input.actorSamples, "Phase8Input.actorSamples");
  // Definition handles are opaque capabilities. Their exact object identity is
  // authority, so never clone or traverse the handle objects as caller-owned
  // plain data.
  const definitions = resolveDefinitions(input.definitionHandles as readonly object[]);
  if (definitions.length !== 7) throw new TypeError("all seven definition handles are required");
  assertPurchaseSnapshots(input.purchaseEligibilitySnapshots);
  assertDeepFrozenPlain(input.purchaseEligibilitySnapshots, "Phase8Input.purchaseEligibilitySnapshots");
  const priorFactsForValidation = new Map(input.previousSlice.currentOverlapFacts.map((fact) => [
    [fact.membershipBaseline.actorId, fact.membershipBaseline.bodyId, fact.membershipBaseline.definitionId, fact.membershipBaseline.triggerId, fact.membershipBaseline.nodeId, fact.membershipBaseline.serviceId].join(":"), fact,
  ]));
  assertArray(input.phase7ArrivalObservations, "phase7ArrivalObservations");
  assertCanonicalOrder(input.phase7ArrivalObservations, (a, b) => {
    const left = a.authorizationKey; const right = b.authorizationKey;
    return left.actorId - right.actorId || left.bodyId - right.bodyId || left.definitionId - right.definitionId ||
      left.triggerId - right.triggerId || left.nodeId - right.nodeId || left.serviceId - right.serviceId ||
      left.serviceVersion - right.serviceVersion;
  }, "arrival observations");
  const observations = new Map<string, Phase7ArrivalObservation>();
  for (const observation of input.phase7ArrivalObservations) {
    assertObservation(observation, input.authorityTick);
    const key = authorizationKey(observation);
    observations.set(key, observation);
  }
  assertDeepFrozenPlain(input.phase7ArrivalObservations, "Phase8Input.phase7ArrivalObservations");
  assertArray(input.opportunities, "opportunities");
  assertCanonicalOrder(input.opportunities, opportunityCompare, "opportunities");
  for (const opportunity of input.opportunities) assertOpportunity(opportunity, input.authorityTick, input.actorSamples, definitions, observations, priorFactsForValidation, previousTick);
  assertDeepFrozenPlain(input.opportunities, "Phase8Input.opportunities");
  const referencedObservationKeys = new Set(input.opportunities.map(authorizationKey));
  assertArray(input.preauthorizedActionGrants, "preauthorizedActionGrants");
  assertCanonicalOrder(input.preauthorizedActionGrants, (a, b) => a.actorId - b.actorId || a.actionId - b.actionId, "preauthorized grants");
  for (const grant of input.preauthorizedActionGrants) assertGrant(grant, input.authorityTick, input.actorSamples, definitions, observations);
  assertDeepFrozenPlain(input.preauthorizedActionGrants, "Phase8Input.preauthorizedActionGrants");
  for (const grant of input.preauthorizedActionGrants) {
    const observation = observations.get(authorizationKey({ authorizationBaseline: grant.authorizationBaseline } as ActionOpportunitySnapshot))!;
    if (observation.status === "eligible" && !input.opportunities.some((opportunity) => deepEqual(opportunity.authorizationBaseline, grant.authorizationBaseline))) {
      throw new TypeError("eligible grant observation lacks its exact opportunity");
    }
  }
  for (const grant of input.preauthorizedActionGrants) referencedObservationKeys.add(authorizationKey({ authorizationBaseline: grant.authorizationBaseline } as ActionOpportunitySnapshot));
  assertArray(input.activeActionAuthorities, "activeActionAuthorities");
  assertCanonicalOrder(input.activeActionAuthorities, (a, b) => a.actorId - b.actorId || a.actionId - b.actionId, "active authorities");
  for (const authority of input.activeActionAuthorities) assertActiveAuthorityShape(authority);
  assertDeepFrozenPlain(input.activeActionAuthorities, "Phase8Input.activeActionAuthorities");
  for (const authority of input.activeActionAuthorities) referencedObservationKeys.add(authorizationKey({ authorizationKey: authority.currentContinuityBaseline.authorizationKey } as Phase7ArrivalObservation));
  if (input.phase7ArrivalObservations.some((observation) => !referencedObservationKeys.has(authorizationKey(observation)))) throw new TypeError("extra Phase7 arrival observation");
  const suppliedPurchases = new Map(input.purchaseEligibilitySnapshots.map((snapshot) => [snapshot.purchaseOfferId, snapshot]));
  for (const opportunity of input.opportunities) {
    if (opportunity.purchaseEligibility !== null && !deepEqual(suppliedPurchases.get(opportunity.purchaseEligibility.purchaseOfferId), opportunity.purchaseEligibility)) throw new TypeError("purchase opportunity/snapshot collection mismatch");
  }
  for (const authority of input.activeActionAuthorities) {
    const eligibility = authority.currentContinuityBaseline.purchaseEligibility;
    if (eligibility !== null && !deepEqual(suppliedPurchases.get(eligibility.purchaseOfferId), eligibility)) throw new TypeError("active purchase/snapshot collection mismatch");
  }

  const cancellations: ActionCancellation[] = [];
  const releases: UnstartedActionGrantRelease[] = [];
  const requests: ActionSelectionRequest[] = [];
  const overlapFacts: OverlapFact[] = [];
  const slots: ActorActionStateSlot[] = [];
  const previews: S03PreviewRequest[] = [];
  const tendReady: TendReady[] = [];
  const purchaseReady: PurchaseDwellReady[] = [];
  const presentation: PresentationEvidenceEvent[] = [];
  const recoveryTokens: TendRecoveryToken[] = [];
  const priorSlots = new Map(input.previousSlice.actionStateSlots.map((slot) => [slot.actorId, slot]));
  const authorityByActor = new Map(input.activeActionAuthorities.map((authority) => [authority.actorId, authority]));
  const priorActiveSlots = input.previousSlice.actionStateSlots.filter((slot) => slot.state !== null);
  if (priorActiveSlots.length !== input.activeActionAuthorities.length || priorActiveSlots.some((slot) => {
    const authority = authorityByActor.get(slot.actorId);
    return authority === undefined || authority.actionId !== slot.state!.actionId;
  })) throw new TypeError("active authority/prior state bijection failure");
  const releaseSources = new Set<string>();
  for (const authority of input.activeActionAuthorities) {
    if (authority.releaseProof !== null) {
      const source = `${authority.releaseProof.sourcePhase}:${authority.releaseProof.sourceSequence}`;
      if (releaseSources.has(source)) throw new TypeError("duplicate release proof source");
      releaseSources.add(source);
    }
  }
  const grantByActor = new Map(input.preauthorizedActionGrants.map((grant) => [grant.actorId, grant]));
  if (grantByActor.size !== input.preauthorizedActionGrants.length) throw new TypeError("at most one grant per actor");
  if ([...grantByActor.keys()].some((actorId) => authorityByActor.has(actorId))) throw new TypeError("actor cannot have active authority and grant");

  const priorFacts = new Map(input.previousSlice.currentOverlapFacts.map((fact) => [
    [fact.membershipBaseline.actorId, fact.membershipBaseline.bodyId, fact.membershipBaseline.definitionId, fact.membershipBaseline.triggerId, fact.membershipBaseline.nodeId, fact.membershipBaseline.serviceId].join(":"),
    fact,
  ]));
  for (const opportunity of input.opportunities) {
    const factKey = [opportunity.actorId, opportunity.bodyId, opportunity.definitionId, opportunity.triggerId, opportunity.nodeId, opportunity.serviceId].join(":");
    const prior = priorFacts.get(factKey);
    const retain = prior !== undefined && previousTick !== null && prior.lastRetainedTick === previousTick && deepEqual(prior.membershipBaseline, opportunity.authorizationBaseline.membershipBaseline);
    overlapFacts.push(cloneFreeze({
      membershipBaseline: opportunity.authorizationBaseline.membershipBaseline,
      distanceSquaredMicroWU2: opportunity.distanceSquaredMicroWU2,
      enteredTick: retain ? prior.enteredTick : input.authorityTick,
      lastRetainedTick: input.authorityTick,
    }));
  }

  const authoritativeRecoveryKeys = new Set(input.previousSlice.tendRecoveryTokens
    .filter((token) => token.status !== "consumed")
    .map(recoveryTokenKey));
  for (const opportunity of input.opportunities) {
    const key = proofKey(opportunity);
    if (key !== null && !authoritativeRecoveryKeys.has(recoveryTokenKey(key))) throw new TypeError("recovery opportunity references no live token");
  }
  for (const grant of input.preauthorizedActionGrants) {
    const key = grant.authorizationBaseline.priorityBasis.tendRecoveryProof?.recoveryTokenKey;
    if (key !== undefined && !authoritativeRecoveryKeys.has(recoveryTokenKey(key))) throw new TypeError("recovery grant references no live token");
    if (key !== undefined) {
      const token = input.previousSlice.tendRecoveryTokens.find((candidate) => recoveryTokenKey(candidate) === recoveryTokenKey(key));
      if (token?.status === "selection-pending" && grant.provenanceKind === "selection-request" && !deepEqual(grant.sourceSelectionRequest, token.selectionRequest)) {
        throw new TypeError("pending recovery selection grant does not derive from stored request");
      }
    }
  }

  const recoveryArbitrations = gap ? [] : liveRecoveryArbitrations(input.authorityTick, input.previousSlice.tendRecoveryTokens, input.opportunities);
  const recoveryByActor = new Map<number, RecoveryArbitration[]>();
  for (const arbitration of recoveryArbitrations) {
    if (arbitration.selected !== null) {
      const actorResources = recoveryByActor.get(arbitration.selected.actorId) ?? [];
      actorResources.push(arbitration);
      recoveryByActor.set(arbitration.selected.actorId, actorResources);
    }
  }

  for (const actor of input.actorSamples) {
    let nextState: ActionState | null = null;
    const priorState = priorSlots.get(actor.actorId)?.state ?? null;
    if (priorState !== null) {
      const authority = authorityByActor.get(actor.actorId)!;
      if (priorState.phase === 3) {
        if (authority.authorityStatus !== "released" || authority.releaseProof?.cause !== "normal-terminal" ||
            authority.releaseProof.sourcePhase !== 6 || authority.explicitCancel ||
            !hasExactTerminalHandoff(priorState, input.previousSlice.phase8Output!)) {
          throw new TypeError("phase3 state requires an exact normal-terminal release and handoff");
        }
      } else if (gap) {
        const cancellation = cancellationFrom(priorState, input.authorityTick, "lifecycle-gap");
        cancellations.push(cancellation);
      }
      else {
        const definition = definitionById(definitions, priorState.definitionId);
        const observation = input.phase7ArrivalObservations.find((candidate) => deepEqual(candidate.authorizationKey, authority.currentContinuityBaseline.authorizationKey));
        const active = validateActiveState(priorState, authority, actor, input.authorityTick, observation, definition);
        if (active.valid) nextState = priorState;
        else if (!active.terminal) {
          const cancellation = cancellationFrom(priorState, input.authorityTick, active.reason!);
          cancellations.push(cancellation);
          if (priorState.actionKind === "tend-wave") recoveryTokens.push(tokenFromCancellation(priorState, cancellation));
        }
      }
    }
    const cancelledThisActor = cancellations.some((cancellation) => cancellation.actorId === actor.actorId);
    if (nextState === null && !gap && (priorState === null || cancelledThisActor)) {
      const actorRecoveries = recoveryByActor.get(actor.actorId) ?? [];
      const actorOpportunities = input.opportunities.filter((opportunity) => {
        if (opportunity.actorId !== actor.actorId) return false;
        const key = proofKey(opportunity);
        if (key === null) return true;
        return actorRecoveries.some((arbitration) => arbitration.selected === opportunity);
      });
      const winner = actorOpportunities.reduce<ActionOpportunitySnapshot | undefined>((best, candidate) => best === undefined || winnerCompare(candidate, best) < 0 ? candidate : best, undefined);
      const actorRecovery = winner === undefined ? undefined : actorRecoveries.find((arbitration) => arbitration.selected === winner);
      const grant = grantByActor.get(actor.actorId);
      if (winner !== undefined && grant !== undefined && !cancelledThisActor && deepEqual(grant.authorizationBaseline, winner.authorizationBaseline) && grant.grantTick === input.authorityTick) {
        if (proofKey(winner) !== null) {
          const receipt = createRecoveryReceipt(input.authorityTick, actorRecovery!.token, grant);
          nextState = startAction(input.authorityTick, grant, receipt);
          recoveryTokens.push(consumedToken(actorRecovery!.token));
        } else nextState = startAction(input.authorityTick, grant);
      } else if (winner !== undefined) {
        const request = cloneFreeze({ selectionTick: input.authorityTick, authorizationBaseline: winner.authorizationBaseline });
        const pendingRecoveryFailure = proofKey(winner) !== null && actorRecovery?.token.status === "selection-pending";
        if (!pendingRecoveryFailure) requests.push(request);
        if (proofKey(winner) !== null) {
          if (actorRecovery!.token.status === "available") recoveryTokens.push(pendingToken(actorRecovery!.token, request));
        }
      }
      if (grant !== undefined && nextState === null) {
        const currentOpportunity = input.opportunities.find((opportunity) => opportunity.actorId === actor.actorId && deepEqual(opportunity.authorizationBaseline, grant.authorizationBaseline));
        const grantProof = grant.authorizationBaseline.priorityBasis.tendRecoveryProof;
        const grantRecovery = grantProof === null ? undefined : recoveryArbitrations.find((entry) => recoveryTokenKey(entry.token) === recoveryTokenKey(grantProof.recoveryTokenKey));
        const contended = currentOpportunity !== undefined && grantRecovery !== undefined && grantRecovery.selected !== currentOpportunity;
        const reason = contended ? "recovery-token-contended" : currentOpportunity === undefined ? "phase7-nonarrival-or-ineligible" : "lost-arbitration";
        let releaseOpportunity: ActionOpportunitySnapshot | null = null;
        if (reason === "lost-arbitration") {
          releaseOpportunity = winner!;
        } else if (reason === "recovery-token-contended") {
          releaseOpportunity = currentOpportunity!;
        }
        releases.push(cloneFreeze({
          releaseTick: input.authorityTick, actorId: grant.actorId, actionId: grant.actionId,
          authorizationBaseline: grant.authorizationBaseline,
          currentOpportunity: releaseOpportunity,
          recoveryTokenKey: reason === "recovery-token-contended" ? grantProof!.recoveryTokenKey : null,
          recoveryResourceOwner: reason === "recovery-token-contended" ? grantRecovery!.owner : null, grant, reason,
        }));
      }
    }
    if (nextState !== null) {
      const progressed = progressAction(nextState, input.authorityTick);
      nextState = progressed.state;
      if (progressed.preview !== null) previews.push(progressed.preview);
      if (progressed.tend !== null) tendReady.push(progressed.tend);
      if (progressed.purchase !== null) purchaseReady.push(progressed.purchase);
      if (progressed.presentation !== null) presentation.push(progressed.presentation);
    }
    slots.push(cloneFreeze({ actorId: actor.actorId, state: nextState }));
  }
  for (const arbitration of recoveryArbitrations) {
    const handled = recoveryTokens.some((token) => recoveryTokenKey(token) === recoveryTokenKey(arbitration.token));
    if (handled) continue;
    if (arbitration.token.status === "selection-pending" && arbitration.selected === null) {
      // Pending tokens expire after this exact bind boundary. Wrong claimants are
      // excluded from per-actor recovery arbitration and their grants were released.
      continue;
    }
  }
  for (const prior of input.previousSlice.actionStateSlots) {
    if (prior.state !== null && !input.actorSamples.some((actor) => actor.actorId === prior.actorId)) {
      const active = input.activeActionAuthorities.find((candidate) => candidate.actorId === prior.actorId)!;
      if (prior.state.phase === 3) {
        if (active.authorityStatus !== "released" || active.releaseProof?.cause !== "normal-terminal" ||
            active.releaseProof.sourcePhase !== 6 || active.explicitCancel ||
            !hasExactTerminalHandoff(prior.state, input.previousSlice.phase8Output!)) {
          throw new TypeError("phase3 state requires an exact normal-terminal release and handoff");
        }
      } else if (gap) {
        const cancellation = cancellationFrom(prior.state, input.authorityTick, "lifecycle-gap");
        cancellations.push(cancellation);
      }
      else {
        const definition = definitionById(definitions, prior.state.definitionId);
        const observation = input.phase7ArrivalObservations.find((candidate) => deepEqual(candidate.authorizationKey, active.currentContinuityBaseline.authorizationKey));
        const result = validateActiveState(prior.state, active, undefined, input.authorityTick, observation, definition);
        if (!result.terminal) {
          const cancellation = cancellationFrom(prior.state, input.authorityTick, result.reason!);
          cancellations.push(cancellation);
          if (prior.state.actionKind === "tend-wave") recoveryTokens.push(tokenFromCancellation(prior.state, cancellation));
        }
      }
    }
  }
  if (gap) {
    for (const grant of input.preauthorizedActionGrants) releases.push(cloneFreeze({
      releaseTick: input.authorityTick, actorId: grant.actorId, actionId: grant.actionId,
      authorizationBaseline: grant.authorizationBaseline, currentOpportunity: null,
      recoveryTokenKey: null, recoveryResourceOwner: null, grant, reason: "lifecycle-gap",
    }));
  }
  cancellations.sort((a, b) => a.actorId - b.actorId);
  recoveryTokens.sort((a, b) => a.priorActorId - b.priorActorId);
  requests.sort((a, b) => a.authorizationBaseline.authorizationKey.actorId - b.authorizationBaseline.authorizationKey.actorId);
  releases.sort((a, b) => a.actorId - b.actorId);
  previews.sort((a, b) => a.actorId - b.actorId);
  tendReady.sort((a, b) => a.actorId - b.actorId);
  purchaseReady.sort((a, b) => a.actorId - b.actorId);
  presentation.sort((a, b) => a.actorId - b.actorId);
  const output = cloneFreeze({
    authorityTick: input.authorityTick,
    currentOverlapFacts: overlapFacts,
    actionStateSlots: slots,
    actionCancellations: cancellations,
    tendRecoveryTokens: recoveryTokens,
    actionSelectionRequests: requests,
    unstartedGrantReleases: releases,
    previewRequests: previews,
    tendReady,
    purchaseDwellReady: purchaseReady,
    presentationEvidence: presentation,
  }) as Phase8Output;
  assertPhase8Output(output, input.authorityTick);
  const slice = cloneFreeze({
    lastPhase8Tick: input.authorityTick,
    currentOverlapFacts: output.currentOverlapFacts,
    actionStateSlots: output.actionStateSlots,
    actionCancellations: output.actionCancellations,
    tendRecoveryTokens: output.tendRecoveryTokens,
    actionSelectionRequests: output.actionSelectionRequests,
    unstartedGrantReleases: output.unstartedGrantReleases,
    phase8Output: output,
    phase9: { status: "pending", authorityTick: input.authorityTick, output: null },
  }) as TriggerActionLifecycleSlice;
  return cloneFreeze({ slice, output }) as Phase8StepResult;
}

const COMMIT_REASONS = new Set([
  "committed", "invalid-command", "cancelled", "actor-missing", "actor-disabled", "actor-not-present",
  "actor-not-humanoid", "action-mismatch", "duplicate-idempotency-key", "duplicate-item-id", "same-owner",
  "owner-missing", "actor-cargo-mismatch", "stale-source-version", "stale-destination-version", "item-missing",
  "source-mismatch", "wrong-item-kind", "capacity-claim-mismatch", "insufficient-capacity", "nav-revision-required",
  "nav-revision-mismatch", "conflicting-claim", "claim-missing", "expired-claim", "version-exhausted",
]);
const OWNER_KINDS = new Set<number>(Object.values(OWNER_KIND));

function validCommitResult(value: unknown): value is CommitResult {
  try {
    assertRecord(value, ["committed", "reason", "state"], "CommitResult");
    if (typeof value.committed !== "boolean" || typeof value.reason !== "string" || !COMMIT_REASONS.has(value.reason) ||
        (value.committed ? value.reason !== "committed" : value.reason === "committed")) return false;
    assertRecord(value.state, ["items", "owners", "actors", "receipts", "events", "currency", "navRevision", "nextEventSequence"], "LedgerState");
    const state = value.state as unknown as LedgerState;
    if (!deepFrozenPlain(state)) return false;
    assertArray(state.items, "LedgerState.items"); assertArray(state.owners, "LedgerState.owners");
    assertArray(state.actors, "LedgerState.actors"); assertArray(state.receipts, "LedgerState.receipts"); assertArray(state.events, "LedgerState.events");
    for (const item of state.items) assertRecord(item, ["itemId", "kind", "lineageId", "ownerKind", "ownerId", "slotIndex", "version"], "LedgerState item");
    for (const owner of state.owners) assertRecord(owner, ["ownerKind", "ownerId", "version", "capacity", "acceptedItemKinds", "navRelevant"], "LedgerState owner");
    for (const actor of state.actors) assertRecord(actor, ["actorId", "actorKind", "actionId", "enabled", "present"], "LedgerState actor");
    for (const receipt of state.receipts) assertRecord(receipt, ["sequence", "idempotencyKey"], "LedgerState receipt");
    for (const event of state.events) assertRecord(event, ["sequence", "eventKind", "reason", "actorId", "actionId", "idempotencyKey", "itemIds"], "LedgerState event");
    const rebuilt = createLedgerState({
      items: state.items,
      owners: state.owners,
      actors: state.actors,
      receipts: state.receipts,
      events: state.events,
      currency: state.currency,
      navRevision: state.navRevision,
      nextEventSequence: state.nextEventSequence,
    });
    if (!deepEqual(rebuilt, state)) return false;
    return true;
  } catch { return false; }
}

function deepFrozenPlain(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value) && !Object.is(value, -0);
  if (typeof value !== "object" || !Object.isFrozen(value) || ancestors.has(value)) return false;
  const prototype = Object.getPrototypeOf(value) as object | null;
  if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) return false;
  const next = new Set(ancestors).add(value);
  return Reflect.ownKeys(value).every((key) => {
    if (Array.isArray(value) && key === "length") return true;
    if (typeof key !== "string") return false;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor !== undefined && descriptor.enumerable && "value" in descriptor && deepFrozenPlain(descriptor.value, next);
  });
}

function proposalFrom(request: S03PreviewRequest): DirectS03TransferProposal {
  const work = request.workFact;
  return cloneFreeze({
    kind: "direct-s03-transfer", authorityTick: request.authorityTick, actorId: request.actorId,
    bodyId: request.bodyId, actionId: request.actionId, serviceId: request.serviceId,
    serviceVersion: request.serviceVersion, triggerId: request.triggerId, nodeId: request.nodeId,
    itemKind: work.itemKind, expectedItemKind: work.expectedItemKind, quantity: work.quantity,
    transferCommand: work.transferCommand,
  });
}

export function reducePhase9(
  input: Phase9Input,
  commitTransfer: (state: LedgerState, command: TransferCommand, leases: LeaseBook | null, tick: number) => CommitResult,
): Phase9StepResult {
  assertRecord(input, ["authorityTick", "phase8Slice", "ledgerState", "leaseBook"], "Phase9Input");
  assertPositive(input.authorityTick, "authorityTick");
  assertSlice(input.phase8Slice);
  if (input.phase8Slice.lastPhase8Tick !== input.authorityTick || input.phase8Slice.phase9.status !== "pending" ||
      input.phase8Slice.phase9.authorityTick !== input.authorityTick || input.phase8Slice.phase8Output === null) throw new TypeError("Phase9 must consume same-tick pending slice");
  if (!validCommitResult({ committed: false, reason: "invalid-command", state: input.ledgerState })) throw new TypeError("Phase9 LedgerState is invalid");
  if (input.leaseBook !== null) {
    assertRecord(input.leaseBook, ["nextLeaseId", "leases"], "LeaseBook"); assertPositive(input.leaseBook.nextLeaseId, "LeaseBook.nextLeaseId"); assertArray(input.leaseBook.leases, "LeaseBook.leases");
    let priorLeaseId = 0;
    for (const lease of input.leaseBook.leases) {
      assertRecord(lease, ["leaseId", "actorId", "actionId", "ownerKind", "ownerId", "ownerVersion", "capacity", "version", "expiresTick"], "CapacityLease");
      assertPositive(lease.leaseId, "leaseId");
      assertPositive(lease.actorId, "actorId");
      assertPositive(lease.actionId, "actionId");
      if (typeof lease.ownerKind !== "number" || !OWNER_KINDS.has(lease.ownerKind)) throw new TypeError("ownerKind is invalid");
      assertPositive(lease.ownerId, "ownerId");
      assertNonNegative(lease.ownerVersion, "ownerVersion");
      assertPositive(lease.capacity, "capacity");
      assertNonNegative(lease.version, "version");
      assertPositive(lease.expiresTick, "expiresTick");
      if (lease.leaseId <= priorLeaseId) throw new TypeError("LeaseBook order invalid");
      priorLeaseId = lease.leaseId;
    }
    if (priorLeaseId >= input.leaseBook.nextLeaseId) throw new TypeError("LeaseBook.nextLeaseId must exceed every leaseId");
    if (!deepFrozenPlain(input.leaseBook)) throw new TypeError("LeaseBook must be recursively immutable");
  }
  const sourcePreviewRequests = input.phase8Slice.phase8Output.previewRequests;
  if (sourcePreviewRequests.some((request) => request.workFact.transferCommand.cancelled !== false)) throw new TypeError("invalid S03 preview request");
  const directTransferProposals: DirectS03TransferProposal[] = [];
  for (const request of sourcePreviewRequests) {
    try {
      const work = request.workFact;
      const result = commitTransfer(input.ledgerState, work.transferCommand, input.leaseBook, input.authorityTick);
      if (validCommitResult(result) && result.committed) directTransferProposals.push(proposalFrom(request));
    } catch { /* A single preview cannot fail the phase. */ }
  }
  const output = cloneFreeze({ authorityTick: input.authorityTick, sourcePreviewRequests, directTransferProposals }) as Phase9Output;
  const slice = cloneFreeze({ ...input.phase8Slice, phase9: { status: "consumed", authorityTick: input.authorityTick, output } }) as TriggerActionLifecycleSlice;
  return cloneFreeze({ slice, output }) as Phase9StepResult;
}
